import { CoverageSettings, Policy } from "@/shared/types/Quote.interface";
import { CoverageMatchCriteria, CoverageUtils, CoverageValue, PresetCoverageLevel } from "@/shared/utils/CoverageUtils";
import { PetPlan } from "@/shared/hooks/usePetPlans";
import Strings from "@/shared/utils/Strings.constants";

export type RecommendedPlanId = "basic" | "standard" | "premium";

type RecommendedPlanPreset = PresetCoverageLevel & {
    id: RecommendedPlanId;
    highlighted?: boolean;
};

const RECOMMENDED_PLAN_PRESETS: RecommendedPlanPreset[] = [
    { id: "basic", name: "Basic", config: { type: ["accident", "illness"], annualLimit: 2500, deductible: 500, reimbursementPercent: 70 } },
    { id: "standard", name: "Standard", config: { type: ["accident", "illness"], annualLimit: 5000, deductible: 500, reimbursementPercent: 80 }, highlighted: true },
    { id: "premium", name: "Premium", config: { type: ["accident", "illness"], annualLimit: 5000, deductible: 250, reimbursementPercent: 80 } }
];

/** The only pet fields plan resolution and pricing read — callers can pass a stable slice of the policy. */
type RecommendedPlanPet = Pick<Policy, "id" | "name" | "species" | "discounts">;

export type ResolvedRecommendedPlan = {
    id: RecommendedPlanId;
    name: string;
    highlighted: boolean;
    annualLimit: number;
    annualDeductible: number;
    reimbursementRate: number;
    config: CoverageSettings;
    /** monthly price for one pet, discounts applied */
    price: number;
};

type RecommendedPlansForPet = {
    petId: string;
    petName: string;
    species?: string;
    plans: ResolvedRecommendedPlan[];
};

/**
 * Resolve the 3 recommended plans for one pet against its fetched plan list.
 * Returns null when any of the 3 plans cannot be resolved (missing plan combo or
 * missing price) — the feature degrades to "never offered" rather than showing a
 * partial set.
 */
const resolveRecommendedPlansForPet = ({
    pet,
    petPlans,
    coverageValuesToMatch
}: {
    pet: RecommendedPlanPet;
    petPlans: PetPlan[];
    coverageValuesToMatch: CoverageMatchCriteria[];
}): RecommendedPlansForPet | null => {
    if (!pet.id || !petPlans?.length) return null;

    const plans: ResolvedRecommendedPlan[] = [];

    for (const preset of RECOMMENDED_PLAN_PRESETS) {
        // one preset per call keeps the preset → result mapping unambiguous
        const matches: CoverageValue[] = CoverageUtils.getPresetPlansFromApiData({ presetCoverageLevels: [preset], petPlans });
        const match = matches[0];
        if (!match?.config) return null;

        const priceResult = CoverageUtils.calculateSinglePetPrice({
            coverageSettings: match.config,
            petPlans,
            coverageValuesToMatch,
            isAnnualBilling: false, // prices display as "$X/mo" regardless of billing frequency
            // The plan list's prices carry no discounts; the pet's active adjustment-based
            // discounts (e.g. 10% multipet) must be applied or the modal price won't match
            // the coverage step's price after the plan is applied.
            policyDiscounts: pet.discounts?.filter(
                (discount): discount is { id?: string; name?: string; adjustment: number; amount: number } => discount.adjustment !== undefined && discount.amount !== undefined
            )
        });
        if (!priceResult) return null;
        plans.push({
            id: preset.id,
            name: preset.name,
            highlighted: !!preset.highlighted,
            annualLimit: preset.config.annualLimit ?? 0,
            annualDeductible: preset.config.deductible ?? 0,
            reimbursementRate: preset.config.reimbursementPercent ?? 0,
            config: match.config,
            price: priceResult.finalPrice
        });
    }

    return { petId: pet.id, petName: pet.name ?? "", species: pet.species, plans };
};

export type RecommendedPlanGroup = {
    /** "all-pets" for the linked group, otherwise the pet id */
    groupKey: string;
    /** header label: pet name, or "N pets" for the linked group */
    header: string;
    species?: string;
    /** pets this group's Select applies to */
    petIds: string[];
    /** plan cards; price is summed across petIds for the linked group */
    plans: ResolvedRecommendedPlan[];
};

/** A linked group applies one config to every pet, so each pet must resolve the same plan settings. */
const haveEquivalentPlanConfigs = (perPet: RecommendedPlansForPet[], planIndex: number): boolean => {
    const [first, ...rest] = perPet;
    const reference = first?.plans[planIndex]?.config;
    if (!reference) return false;
    return rest.every(entry => {
        const candidate = entry.plans[planIndex]?.config;
        if (!candidate) return false;
        return (
            candidate.amounts?.annualLimit === reference.amounts?.annualLimit &&
            candidate.amounts?.annualDeductible === reference.amounts?.annualDeductible &&
            candidate.amounts?.reimbursementRate === reference.amounts?.reimbursementRate &&
            JSON.stringify(candidate.coverages?.map(coverage => coverage.id).sort()) === JSON.stringify(reference.coverages?.map(coverage => coverage.id).sort())
        );
    });
};

/**
 * Build the modal's card groups per workflow:
 * - single pet → one group for that pet
 * - multi-pet, apply-to-all ON → one "N pets" group (prices summed across pets)
 * - multi-pet, apply-to-all OFF → one group per pet
 * Returns null when any in-scope pet cannot resolve all 3 plans (degradation rule).
 */
export const buildRecommendedPlanGroups = ({
    pets,
    petPlans,
    coverageValuesToMatch,
    isMultiPetLinked
}: {
    pets: RecommendedPlanPet[];
    petPlans?: Record<string, PetPlan[]>;
    coverageValuesToMatch: CoverageMatchCriteria[];
    isMultiPetLinked: boolean;
}): RecommendedPlanGroup[] | null => {
    if (!pets.length || !petPlans) return null;

    const perPet: RecommendedPlansForPet[] = [];
    for (const pet of pets) {
        const resolved = resolveRecommendedPlansForPet({ pet, petPlans: petPlans[pet.id ?? ""] ?? [], coverageValuesToMatch });
        if (!resolved) return null;
        perPet.push(resolved);
    }

    // The linked group applies one pet's config to every policy, so only offer it when
    // every pet resolved each preset to equivalent settings. Otherwise fall back to
    // per-pet groups, which apply each pet's own resolved config.
    const canApplyOneConfigToAllPets = perPet[0]!.plans.every((_plan, planIndex) => haveEquivalentPlanConfigs(perPet, planIndex));

    if (pets.length > 1 && isMultiPetLinked && canApplyOneConfigToAllPets) {
        const [first, ...rest] = perPet;
        const summedPlans = first!.plans.map((plan, planIndex) => ({
            ...plan,
            price: rest.reduce((total, petPlansEntry) => total + (petPlansEntry.plans[planIndex]?.price ?? 0), plan.price)
        }));
        return [
            {
                groupKey: "all-pets",
                header: Strings.MULTI_PLAN_GUIDE.PETS_GROUP_HEADER(pets.length),
                petIds: perPet.map(entry => entry.petId),
                plans: summedPlans
            }
        ];
    }

    return perPet.map(entry => ({
        groupKey: entry.petId,
        header: entry.petName,
        species: entry.species,
        petIds: [entry.petId],
        plans: entry.plans
    }));
};
